import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SaveFilterDialog from './dialogs/SaveFilterDialog';
import { apiGet, apiPost, authenticatedFetch } from '../utils/apiUtils';
import supabase from '../utils/supabaseClient';
import LoadingOverlay from './common/LoadingOverlay';

/**
 * FilterResults component - displays filtering results and allows export/override functionality
 * Uses forwardRef to expose overrides data to parent components
 */
const FilterResults = forwardRef(function FilterResults({ 
  onReset,
  onBack,
  setLoading,
  filterId,
  filterResults,
  setFilterResults,
  userOverrides,
  setUserOverrides,
  filterProgress, // Pass along from parent component
  setFilterProgress // Pass along from parent component
}, ref) {
  const [tabIndex, setTabIndex] = useState(2);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [columnFilter, setColumnFilter] = useState('all'); // State for column filter
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [saveDialogOpen, setSaveDialogOpen] = useState(false); // State for save dialog
  // Store the standardized filter config for passing to SaveFilterDialog
  const [standardizedFilterConfig, setStandardizedFilterConfig] = useState(null);
  // RACE CONDITION FIX: Track number of fetch attempts at component level
  const fetchAttemptRef = useRef(0);
  // Removed duplicate declaration of filterProgress state
  
  // Create a function to get formatted overrides
  const getCurrentOverrides = () => {
    // If we don't have any results or user overrides, return empty object
    if (!filterResults || !userOverrides) {
      console.log('No filter results or user overrides available', { filterResults: !!filterResults, userOverrides });
      return {};
    }
    
    console.log('Raw user overrides:', userOverrides);
    
    // Format overrides for storage
    // We need a structure like: { columnName: { value: decision, ... }, ... }
    const formattedOverrides = {};
    
    // Loop through all override entries
    Object.entries(userOverrides).forEach(([key, decision]) => {
      // Key format is "column|value"
      const [column, value] = key.split('|');
      
      // Initialize column object if it doesn't exist
      if (!formattedOverrides[column]) {
        formattedOverrides[column] = {};
      }
      
      // Add the decision for this value
      formattedOverrides[column][value] = decision;
    });
    
    console.log('Formatted overrides for saving:', formattedOverrides);
    return formattedOverrides;
  };

  // Expose methods to parent components using useImperativeHandle
  useImperativeHandle(ref, () => ({
    // Method to get current overrides in the correct format for saving
    getCurrentOverrides
  }));

  // Emergency reset function to clear all application state
  // Function preserved in code but button removed from UI
  // eslint-disable-next-line no-unused-vars
  const handleEmergencyReset = () => {
    // Clear all localStorage data
    localStorage.clear();
    
    // Show alert to inform user
    alert('Application has been reset. You will be redirected to the start page.');
    
    // Redirect to home page
    window.location.href = '/';
  };

  // Fetch results when component mounts or filterId changes
  useEffect(() => {
    if (filterId) {
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId]);
  
  // Create standardized filter config when filter results change
  useEffect(() => {
    if (filterResults) {
      // CRITICAL DEBUGGING: We need to see the EXACT structure of the filter configuration
      console.log('-------------------------------------------------------------');
      console.log('DEBUG - ENTIRE filterResults object:', JSON.stringify(filterResults, null, 2));
      console.log('DEBUG - Looking for column instructions specifically:');
      
      // If filterConfig exists, examine its structure in detail
      if (filterResults.filterConfig) {
        // Check if Title column exists directly at top level
        if (filterResults.filterConfig.Title) {
          console.log('DEBUG - Title found directly at top level:', filterResults.filterConfig.Title);
        }
        
        // Check if column instructions exist in nested columnConfig structure
        if (filterResults.filterConfig.columnConfig && filterResults.filterConfig.columnConfig.Title) {
          console.log('DEBUG - Title found in columnConfig:', filterResults.filterConfig.columnConfig.Title);
        }
        
        // Look at exactly what's in selectedColumns
        if (filterResults.filterConfig.selectedColumns) {
          console.log('DEBUG - selectedColumns contains:', filterResults.filterConfig.selectedColumns);
        }
      }
      
      // IMPORTANT: We need to preserve ONLY the columns that were specifically selected by the user
      // NOT all columns from the dataset
      let columns = new Set();
      
      // Step 1: Get columns from the original filter configuration (these are the ones the user chose)
      if (filterResults.filterConfig && Array.isArray(filterResults.filterConfig.selectedColumns)) {
        filterResults.filterConfig.selectedColumns.forEach(col => columns.add(col));
        console.log('Using columns from original filter config:', filterResults.filterConfig.selectedColumns);
      } 
      // In case the original selected columns are not available, use a fallback
      else if (filterResults.valueDecisions) {
        // Only take columns where we have actual decisions (not empty objects)
        Object.keys(filterResults.valueDecisions).forEach(col => {
          if (Object.keys(filterResults.valueDecisions[col] || {}).length > 0) {
            columns.add(col);
          }
        });
        console.log('Fallback: Using value decision columns');
      }
      
      // Get user overrides - these contain columns explicitly chosen by the user
      const currentOverrides = ref.current?.getCurrentOverrides ? ref.current.getCurrentOverrides() : {};
      
      if (currentOverrides) {
        Object.keys(currentOverrides).forEach(col => columns.add(col));
      }
      
      console.log('Extracted columns from all sources:', Array.from(columns));
      
      // Get reasoning text and model from localStorage if available
      const reasoningText = localStorage.getItem('filterReasoningText') || '';
      const model = localStorage.getItem('filterModel') || 'o4-mini-high';
      const chunkSize = parseInt(localStorage.getItem('filterChunkSize'), 10) || 300;
      
      // Convert columns Set to Array
      const columnsArray = Array.from(columns);
      
      // Simplest approach - find the filterToolState in localStorage without relying on user ID
      let localStorageData = {};
      // Look for any filterToolState keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('filterToolState_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && data.filterConfig) {
              localStorageData = data;
              break; // Use the first valid filterConfig we find
            }
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
      console.log('--------- CHECKING LOCAL STORAGE ---------');
      if (localStorageData.filterConfig) {
        columnsArray.forEach(column => {
          if (localStorageData.filterConfig[column]) {
            console.log(`LOCAL STORAGE has instructions for ${column}:`, localStorageData.filterConfig[column]);
          }
        });
      }
      
      // Create column config that preserves existing column instructions
      const columnConfig = {};

      // CRITICAL FIX: First, try to get the explicitly saved column config from localStorage
      let savedColumnConfig = {};
      try {
        const savedColumnConfigStr = localStorage.getItem('filterColumnConfig');
        if (savedColumnConfigStr) {
          savedColumnConfig = JSON.parse(savedColumnConfigStr);
          
          // DIAGNOSTIC LOG: Specifically check what column instructions were retrieved
          console.log('=================== DIAGNOSTIC CHECKPOINT ===================');
          console.log('RETRIEVED FROM LOCALSTORAGE IN FILTER RESULTS:');
          Object.keys(savedColumnConfig).forEach(column => {
            if (savedColumnConfig[column] && savedColumnConfig[column].instructions) {
              console.log(`  - ${column}: "${savedColumnConfig[column].instructions}"`);
            } else {
              console.log(`  - ${column}: <no instructions>`);
            }
          });
          console.log('============================================================');
        } else {
          console.log('=================== DIAGNOSTIC CHECKPOINT ===================');
          console.log('NO filterColumnConfig FOUND IN LOCALSTORAGE!');
          console.log('============================================================');
        }
      } catch (e) {
        console.error('Error parsing saved column config from localStorage:', e);
      }
      
      // Get column names directly from llmValueDecisions - our single source of truth for columns
      const columnsFromLLM = filterResults.llmValueDecisions ? Object.keys(filterResults.llmValueDecisions) : [];
      console.log('Using columns directly from llmValueDecisions:', columnsFromLLM);
      
      // Build column config using localStorage instructions as single source of truth
      columnsFromLLM.forEach(column => {
        // Get instructions from localStorage if available, otherwise use empty string
        columnConfig[column] = savedColumnConfig[column] || { instructions: '' };
        console.log(`Column ${column} config from localStorage: ${columnConfig[column].instructions ? `"${columnConfig[column].instructions.substring(0, 30)}${columnConfig[column].instructions.length > 30 ? '...' : ''}"` : '<empty>'}`)
      });
      
      // Use these columns for the final config
      const columnsToUse = columnsFromLLM.length > 0 ? columnsFromLLM : columnsArray;
      
      // Create standardized filter config
      const config = {
        selectedColumns: columnsToUse,
        reasoningText,
        model,
        chunkSize,
        columnConfig
      };
      
      console.log('Created standardized filter config:', config);
      setStandardizedFilterConfig(config);
    }
  }, [filterResults, userOverrides, ref]);

  // Poll for results if processing
  useEffect(() => {
    let resultsIntervalId;
    let progressIntervalId;
    
    if (polling && filterId) {
      // Poll for completed results every 3 seconds
      resultsIntervalId = setInterval(() => {
        fetchResults(false);
      }, 3000);
      
      // Poll for progress more frequently (every 1 second) for smoother updates
      progressIntervalId = setInterval(async () => {
        try {
          // Ensure we have a valid token
          const { data } = await supabase.auth.getSession();
          if (!data?.session?.access_token) {
            await supabase.auth.refreshSession();
          }
          
          // Fetch progress data
          const response = await apiGet(`/api/progress/${filterId}`);
          if (response.ok) {
            const progressData = await response.json();
            if (setFilterProgress) {
              setFilterProgress(progressData);
            }
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }, 1000);
    }
    
    return () => {
      if (resultsIntervalId) {
        clearInterval(resultsIntervalId);
      }
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, filterId]);

  // Fetch filter results from the API
  const fetchResults = async (showLoading = true) => {
    // OVERRIDE FIX: Enhanced check to ensure we always prioritize the saved filter ID
    const applySavedFilterMode = localStorage.getItem('applyingSavedFilter') === 'true';
    const existingFilterId = localStorage.getItem('currentFilterId');
    // Always use existing filter when in saved filter mode, regardless of current filterId
    const usingExistingFilterId = applySavedFilterMode && existingFilterId;
    
    console.log('OVERRIDE-FLOW:', `fetchResults check for saved filter mode:`, {
      applySavedFilterMode,
      existingFilterId,
      currentFilterId: filterId,
      usingExistingFilterId
    });
    
    if (usingExistingFilterId) {
      console.log('OVERRIDE-FLOW:', `Using existing filter ID from saved filter: ${existingFilterId}`);
      // Store the effective filter ID we're actually using
      const effectiveFilterId = existingFilterId;
      
      // Track which filter ID is actually in use for debugging purposes
      if (filterId && filterId !== effectiveFilterId) {
        console.log('OVERRIDE-FLOW:', `IMPORTANT: Overriding new filter ID ${filterId} with saved filter ID ${effectiveFilterId}`);
      }
      
      // Set this filter ID as a global variable that will be used in subsequent operations
      localStorage.setItem('currentFilterInUse', effectiveFilterId);
      
      // We need to use a consistent API approach to avoid inconsistencies
      setLoading(true);
      
      try {
        // Make direct API call to get results for the effective filter ID
        const response = await apiGet(`/api/results/${effectiveFilterId}`);
        
        // Always clean up the flag once we've handled it
        localStorage.removeItem('applyingSavedFilter');
        
        if (!response.ok) {
          throw new Error('Failed to load results for saved filter');
        }
        
        const responseData = await response.json();
        setLoading(false);
        
        if (responseData.status === 'processing') {
          // Job is still processing - set polling flag and clear any errors
          setPolling(true);
          setError(null);
          console.log(`OVERRIDE-FLOW: Filter ${effectiveFilterId} is still processing - polling for updates`);
        } else {
          // Job is complete - stop polling and set results
          setPolling(false);
          setFilterResults(responseData);
          console.log(`OVERRIDE-FLOW: Successfully loaded saved filter ${effectiveFilterId}`);
        }
      } catch (error) {
        setLoading(false);
        console.error('OVERRIDE-FLOW: Error fetching saved filter results:', error);
        setError(error.message || 'Failed to load saved filter results');
      }
      
      return; // Exit early - we're handling this separately
    }
    
    // Normal flow - check if we have a filter ID to fetch
    if (!filterId) {
      console.log('OVERRIDE-FLOW: No filter ID available, skipping fetch');
      return;
    }
    
    // Ensure we have a valid token before proceeding
    const ensureValidToken = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data?.session?.access_token) {
        console.warn('[AUTH-DEBUG] No valid session before making fetch request');
        // Attempt to refresh the session
        await supabase.auth.refreshSession();
        console.log('[AUTH-DEBUG] Session refreshed');
      } else {
        console.log('[AUTH-DEBUG] Valid token confirmed before making request');
      }
    };
    
    await ensureValidToken();
    
    // RACE CONDITION FIX: Track attempt count for current filterId
    fetchAttemptRef.current += 1;
    const currentAttempt = fetchAttemptRef.current;
    
    // Add detailed logging to help debug race conditions
    console.log(`[FRONTEND-DEBUG] Fetching results for ${filterId}, attempt #${currentAttempt}`);
    console.log(`[FRONTEND-DEBUG] Polling status: ${polling ? 'ACTIVE' : 'INACTIVE'}`);
    console.log(`[FRONTEND-DEBUG] Job creation timestamp: ${filterId.replace('filter_', '')}`);
    
    // If this is a filterId newly created within the last second, implement a small delay
    // This gives the backend time to properly create the job before frontend tries to fetch
    if (currentAttempt === 1 && filterId.startsWith('filter_')) {
      const jobTimestamp = parseInt(filterId.replace('filter_', ''));
      const now = Date.now();
      const msSinceCreation = now - jobTimestamp;
      
      console.log(`[FRONTEND-DEBUG] Job age: ${msSinceCreation}ms`);
      
      // For very new jobs, add a small delay before first fetch to prevent race condition
      if (msSinceCreation < 300) {
        const delayMs = 300 - msSinceCreation;
        console.log(`[FRONTEND-DEBUG] Recent job detected, adding ${delayMs}ms delay before first fetch`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Use authenticated API call
      const response = await apiGet(`/api/results/${filterId}`);
      console.log(`[FRONTEND-DEBUG] API response status:`, response.status); 
      
      if (showLoading) {
        setLoading(false);
      }
      
      if (!response.ok) {
        // RACE CONDITION FIX: Additional check for very recent jobs (<1 second old)
        // Even if we get a 404, we should keep polling for a short time if job is very new
        if (response.status === 404 && filterId.startsWith('filter_')) {
          const jobTimestamp = parseInt(filterId.replace('filter_', ''));
          const now = Date.now();
          const msSinceCreation = now - jobTimestamp;
          
          if (msSinceCreation < 1000) {
            console.log(`[FRONTEND-DEBUG] 404 for very recent job (${msSinceCreation}ms old), treating as 'processing'`);
            setPolling(true);
            setError(null);
            return; // Exit early but keep polling
          }
        }
      
        const errorData = await response.json();
        // Handle error - might be authentication or not found
        if (response.status === 404) {
          // Filter not found - likely a session/auth issue
          throw new Error('Previous filter process was not found. Please configure your filters again.');
        } else {
          // Other errors
          throw new Error(errorData.error || `Failed with status: ${response.status}`);
        }
      }
      
      const responseData = await response.json();
      console.log(`[FRONTEND-DEBUG] Response data:`, responseData);
      
      // FIXED: The race condition issue - properly handle processing status
      if (responseData.status === 'processing') {
        // Job is still processing - set polling flag and clear any errors
        setPolling(true);
        setError(null); // Clear any existing errors
        
        // Show processing status message instead of error
        console.log(`[FRONTEND-DEBUG] Filter job ${filterId} is still processing - polling for updates`);
        // If we have progress information, display it
        if (responseData.progress) {
          console.log(`[FRONTEND-DEBUG] Progress: ${responseData.progress.progress}%, Status: ${responseData.progress.status}`);
        }
      } else {
        // Job is complete - stop polling and set results
        console.log(`[FRONTEND-DEBUG] Job complete, storing results`);
        setPolling(false);
        setFilterResults(responseData);
        
        // Store the current filter ID in localStorage for continuity
        if (filterId) {
          console.log('FILTER-ID-CONTINUITY: Storing current filter ID in localStorage:', filterId);
          localStorage.setItem('currentFilterInUse', filterId);
        }
      }
    } catch (error) {
      if (showLoading) {
        setLoading(false);
      }
      console.error('[FRONTEND-DEBUG] Error fetching results:', error);
      
      // RACE CONDITION FIX: For very new jobs, if we get a not found error, assume it's processing
      if (error.message?.includes('not found') && filterId.startsWith('filter_')) {
        const jobTimestamp = parseInt(filterId.replace('filter_', ''));
        const now = Date.now();
        const msSinceCreation = now - jobTimestamp;
        
        if (msSinceCreation < 1000) {
          console.log(`[FRONTEND-DEBUG] Error for very recent job (${msSinceCreation}ms old), treating as 'processing'`);
          setPolling(true);
          setError(null);
          return; // Exit early but keep polling
        }
      }
      
      // Only set error and redirect if this is NOT happening during background polling
      // This prevents the race condition where a "not found" error might appear during processing
      if (!polling || showLoading) {
        setError(error.message || 'Failed to load filter results. Please try again.');
        
        // If filter wasn't found and this is not a background polling call,
        // redirect back to filtering screen, but with safeguards for very new filters
        if (error.message?.includes('not found') && showLoading) {
          const isVeryNewJob = filterId.startsWith('filter_') && 
                               (Date.now() - parseInt(filterId.replace('filter_', ''))) < 2000;
          
          if (!isVeryNewJob) {
            console.log(`[FRONTEND-DEBUG] Job not found and not a new job, redirecting to filter config`);
            // Show alert to better inform user
            alert('Previous filter process was not found. Please configure your filters again.');
            onBack(); // Go back to previous step
          } else {
            console.log(`[FRONTEND-DEBUG] Not redirecting for new job, waiting for processing`);
            setPolling(true); // Force polling for new jobs
          }
        }
      }
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Handle save filter button click
  const handleSaveClick = () => {
    // If we don't have a valid standardizedFilterConfig yet but we do have filterResults,
    // build it from the results data directly
    if ((!standardizedFilterConfig || !standardizedFilterConfig.selectedColumns || standardizedFilterConfig.selectedColumns.length === 0) && filterResults) {
      console.log('Creating standardizedFilterConfig from filterResults before opening save dialog');
      
      // Get column names from llmValueDecisions - single source of truth for columns
      const columnsFromLLM = filterResults.llmValueDecisions ? Object.keys(filterResults.llmValueDecisions) : [];
      console.log('Using columns directly from llmValueDecisions for save dialog:', columnsFromLLM);
      
      // Get reasoningText and model from localStorage if available
      const reasoningText = localStorage.getItem('filterReasoningText') || '';
      const model = localStorage.getItem('filterModel') || 'o4-mini-high';
      const chunkSize = parseInt(localStorage.getItem('filterChunkSize'), 10) || 300;
      
      // Retrieve column instructions from localStorage - single source of truth
      let savedColumnConfig = {};
      try {
        const savedColumnConfigStr = localStorage.getItem('filterColumnConfig');
        if (savedColumnConfigStr) {
          savedColumnConfig = JSON.parse(savedColumnConfigStr);
          console.log('Retrieved column instructions from localStorage for save dialog');
        }
      } catch (e) {
        console.error('Error parsing saved column config from localStorage:', e);
      }
      
      // Create column config using instructions from localStorage
      const columnConfig = {};
      columnsFromLLM.forEach(column => {
        columnConfig[column] = savedColumnConfig[column] || { instructions: '' };
      });
      
      // Create a minimal but valid config for saving
      const tempConfig = {
        selectedColumns: columnsFromLLM,
        reasoningText,
        model,
        chunkSize,
        columnConfig
      };
      
      console.log('Created temporary config for saving:', tempConfig);
      setStandardizedFilterConfig(tempConfig);
      setSaveDialogOpen(true);
      return;
    }
    
    // Standard path - we have valid config
    setSaveDialogOpen(true);
  };

  // Export results as CSV or Excel
  const handleExport = async (format) => {
    if (!filterId) {
      setError('No filter ID found. Please run a filter first.');
      return;
    }
    
    // Get the current filter ID from state to ensure we're using the most recent one
    const currentFilterId = filterId;
    console.log(`Exporting data for filter ID: ${currentFilterId} in ${format} format`);
    
    try {
      setLoading(true);
      
      // Use authenticated fetch for blob response
      console.log(`Making export request to: /api/export/${currentFilterId}?format=${format}`);
      const response = await authenticatedFetch(`/api/export/${currentFilterId}?format=${format}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Export failed with status: ${response.status}`);
      }
      
      // Get the blob data
      const blobData = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blobData);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `filtered_data.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Export error:', error);
      setError(error.message || 'Failed to export results. Please try again.');
    }
  };

  // Direct clicking on chips has replaced context menu approach
  
  // Handle direct click on decision chip to toggle
  const handleDecisionClick = (value, column, newDecision) => {
    // Call the existing override function with all necessary data directly
    handleOverrideDecision(newDecision, column, value);
  };

  // Handle override decision
  const handleOverrideDecision = async (decision, columnParam, valueParam) => {
    // Get the most accurate filter ID to use - prioritize the one in localStorage
    // This is critical to maintain continuity between filter jobs and overrides
    const currentFilterInLocalStorage = localStorage.getItem('currentFilterInUse');
    const effectiveFilterId = currentFilterInLocalStorage || filterId;
    
    console.log('OVERRIDE-FLOW:', `Override decision using filter ID:`, {
      fromProps: filterId,
      fromLocalStorage: currentFilterInLocalStorage,
      effectiveFilterId: effectiveFilterId
    });
    
    // Validate inputs
    if (!decision || !columnParam || valueParam === undefined || !effectiveFilterId) {
      console.error('Invalid override parameters or missing filter ID');
      return;
    }

    try {
      console.log('OVERRIDE-FLOW:', `Processing ${decision} override on ${columnParam}`);
      
      // Submit override to API using authenticated POST
      // Create the proper overrides format expected by the backend
      // Use the effectiveFilterId to ensure continuity
      console.log('OVERRIDE-FLOW:', `Sending override request with filter ID: ${effectiveFilterId}`);
      const response = await apiPost('/api/overrides', {
        filterId: effectiveFilterId, // Use the effective filter ID here
        overrides: {
          column: columnParam,
          value: valueParam,
          decision
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Override failed with status: ${response.status}`);
      }

      // Get response data
      const responseData = await response.json();
      console.log('Override successful:', responseData);
      
      // Important: Update the userOverrides state with this new override
      // Create a key in the format "column|value"
      const overrideKey = `${columnParam}|${valueParam}`;
      
      // Update userOverrides state with the new decision
      setUserOverrides(prevOverrides => ({
        ...prevOverrides,
        [overrideKey]: decision
      }));
      
      console.log('Updated userOverrides with new override:', {
        column: columnParam,
        value: valueParam,
        decision,
        overrideKey
      });
      
      // Refresh results - without additional loading indicators
      await fetchResults(false);
      
      // Hide loading indicator
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Override error:', error);
      setError(error.message || 'Failed to update decision. Please try again.');
    }
  };

  // Get grid columns for data tables
  const getGridColumns = (dataType) => {
    if (!filterResults) return [];
    
    const firstRow = filterResults[dataType]?.[0] || {};
    const columns = Object.keys(firstRow).filter(key => key !== '_reasons');
    
    // For tables with many columns, use smaller fixed widths
    const columnWidth = columns.length > 10 ? 120 : 150;
    
    return columns.map(field => {
      // Handle specific column types
      if (field.toLowerCase().includes('email_score')) {
        return {
          field,
          headerName: field,
          width: 100, // Smaller width for email score columns
          minWidth: 80
        };
      }
      
      // Give more space to description fields
      if (field.toLowerCase().includes('description') || 
          field.toLowerCase().includes('comment') || 
          field.toLowerCase().includes('notes')) {
        return {
          field,
          headerName: field,
          width: 250,
          minWidth: 200
        };
      }
      
      // Default column definition
      return {
        field,
        headerName: field,
        width: columnWidth,
        minWidth: 100
      };
    });
  };

  // Filter rows based on search query
  const getFilteredRows = (rows, fieldsToSearch) => {
    if (!searchQuery.trim()) return rows;
    
    const query = searchQuery.toLowerCase().trim();
    
    return rows.filter(row => {
      // Search across all specified fields
      return fieldsToSearch.some(field => {
        const value = row[field];
        // Handle different value types
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  };

  // Helper function to determine if a decision can be toggled and what the new value would be
  const getDecisionToggleInfo = (rowData) => {
    const currentDecision = rowData.decision;
    const isUserOverride = rowData.reason?.startsWith('User override');
    const newDecision = currentDecision === 'KEEP' ? 'EXCLUDE' : 'KEEP';
    
    return {
      currentDecision,
      isUserOverride,
      newDecision
    };
  };
  
  // Create decision grid columns
  const getDecisionGridColumns = () => {
    return [
      { field: 'column', headerName: 'Column', width: 150 },
      { field: 'value', headerName: 'Value', width: 200 },
      { 
        field: 'decision',
        headerName: 'Decision',
        width: 120,
        renderCell: (params) => {
          // Use our helper function to get decision info
          const { currentDecision, newDecision } = getDecisionToggleInfo(params.row);
          
          // Handle click on the decision chip
          const onChipClick = (event) => {
            event.stopPropagation(); // Prevent row selection
            handleDecisionClick(params.row.value, params.row.column, newDecision);
          };
          
          return (
            <Chip 
              label={currentDecision} 
              size="small"
              onClick={onChipClick}
              clickable
              sx={{ 
                cursor: 'pointer', 
                fontWeight: 600,
                bgcolor: currentDecision === 'KEEP' ? 'wyspur.blue' : 'wyspur.pink', // Using Wyspur blue and pink
                color: '#fff',
                '&:hover': { opacity: 0.9 },
                transition: 'all 0.2s ease',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                padding: '2px 8px',
                borderRadius: '12px'
              }}
            />
          );
        }
      },
      { field: 'reason', headerName: 'Reason', width: 300, minWidth: 200 },
      { field: 'frequency', headerName: 'Frequency', width: 100 }
    ];
  };

  // Create rows for decisions grid
  const getDecisionGridRows = () => {
    if (!filterResults?.llmValueDecisions) return [];
    
    const rows = [];
    let id = 0;
    
    Object.entries(filterResults.llmValueDecisions).forEach(([column, values]) => {
      Object.entries(values).forEach(([value, info]) => {
        rows.push({
          id: id++,
          column,
          value,
          decision: info.decision,
          reason: info.reason || '',
          frequency: info.frequency || 0
        });
      });
    });
    
    return rows;
  };

  // Render loading or processing state with enhanced progress tracking
  if (!filterResults || polling) {
    return (
      <Box sx={{ position: 'relative', minHeight: '300px' }}>
        <LoadingOverlay 
          isLoading={true} 
          progress={filterProgress || {
            status: 'processing',
            message: 'Processing your data...',
            progress: 0,
            columnsInProgress: [],
            columnStatuses: {}
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, overflow: 'hidden' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {/* Header removed to avoid redundancy with page title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Export Excel with All Data (Kept, Excluded, Decisions)">
            <IconButton onClick={() => handleExport('xlsx')}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {/* Refresh button removed as functionality is not needed */}
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Card className="kpi" sx={{ 
          minWidth: 200, 
          flex: '1 1 0px', // Equal width for all cards
          bgcolor: 'wyspur.surface',
          borderBottom: '2px solid',
          borderColor: 'wyspur.border', // Default border color
          boxShadow: 'none'
        }}>
          <CardContent>
            <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
              Total Rows
            </Typography>
            <Typography variant="h4" color="text.primary" align="center">
              {filterResults?.stats?.totalRows || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card className="kpi kpi--kept" sx={{ 
          minWidth: 200, 
          flex: '1 1 0px', // Equal width for all cards
          bgcolor: 'wyspur.surface',
          borderBottom: '2px solid',
          borderColor: 'wyspur.blue', // Wyspur blue bottom border
          boxShadow: 'none'
        }}>
          <CardContent>
            <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
              Kept Rows
            </Typography>
            <Typography variant="h4" color="wyspur.blue" align="center">
              {filterResults?.stats?.keptRowCount || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card className="kpi kpi--excluded" sx={{ 
          minWidth: 200, 
          flex: '1 1 0px', // Equal width for all cards
          bgcolor: 'wyspur.surface',
          borderBottom: '2px solid',
          borderColor: 'wyspur.pink', // Wyspur pink bottom border
          boxShadow: 'none'
        }}>
          <CardContent>
            <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
              Excluded Rows
            </Typography>
            <Typography variant="h4" color="wyspur.pink" align="center">
              {filterResults?.stats?.excludedRowCount || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      <Paper variant="outlined" sx={{ p: 0, mb: 3, bgcolor: 'wyspur.surface', borderColor: 'wyspur.border', width: '1400px', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', borderBottom: 1, borderColor: 'wyspur.border' }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            textColor="text.primary"
            aria-label="filter results tabs"
            sx={{
              flex: 1,
              '& .MuiTabs-indicator': {
                backgroundColor: 'wyspur.purple', // Use Wyspur purple for active tab
              },
              '& .Mui-selected': {
                color: 'text.primary',
                fontWeight: 600,
              }
            }}
          >
            <Tab label="Kept Data" />
            <Tab label="Excluded Data" />
            <Tab label="Value Decisions" />
          </Tabs>

          {/* Search Bar - Show on all tabs */}
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', px: 2 }}>
            <TextField
              size="small"
              placeholder="Search in results..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%', maxWidth: '350px' }}
            />
          </Box>

          {/* Column Filter Dropdown - Only show for Value Decisions tab */}
          {tabIndex === 2 && (
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 2, flex: '0 0 auto' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="column-filter-label">Filter Column</InputLabel>
                <Select
                  labelId="column-filter-label"
                  value={columnFilter}
                  label="Filter Column"
                  onChange={(e) => setColumnFilter(e.target.value)}
                  size="small"
                >
                  <MenuItem value="all">Show All Columns</MenuItem>
                  {Object.keys(filterResults?.llmValueDecisions || {}).map(column => (
                    <MenuItem key={column} value={column}>{column}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        
        <Box sx={{ p: 0, width: '100%' }}>
          {/* Kept Data Tab */}
          {tabIndex === 0 && (
            <Box sx={{ height: 500, width: '1400px', overflow: 'auto' }}>
              <DataGrid
                rows={getFilteredRows(
                  (filterResults?.keptData || []).map((row, index) => ({ id: index, ...row })),
                  Object.keys(filterResults?.keptData?.[0] || {})
                )}
                columns={getGridColumns('keptData')}
                pagination={false}
                hideFooter={true}
                disableSelectionOnClick
                density="compact"
                sx={{ 
                  border: 'none',
                  height: '100%'
                }}
              />
            </Box>
          )}
          
          {/* Excluded Data Tab */}
          {tabIndex === 1 && (
            <Box sx={{ height: 500, width: '1400px', overflow: 'auto' }}>
              <DataGrid
                rows={getFilteredRows(
                  (filterResults?.excludedData || []).map((row, index) => ({ id: index, ...row })),
                  Object.keys(filterResults?.excludedData?.[0] || {})
                )}
                columns={getGridColumns('excludedData')}
                pagination={false}
                hideFooter={true}
                disableSelectionOnClick
                density="compact"
                sx={{ 
                  border: 'none',
                  height: '100%'
                }}
              />
            </Box>
          )}
          
          {/* Value Decisions Tab */}
          {tabIndex === 2 && (
            <Box sx={{ height: 500, width: '1400px', overflow: 'auto' }}>
              <DataGrid
                rows={getFilteredRows(
                  columnFilter === 'all' 
                    ? getDecisionGridRows() 
                    : getDecisionGridRows().filter(row => row.column === columnFilter),
                  ['column', 'value'] // Remove 'reason' from search fields
                )}
                columns={getDecisionGridColumns()}
                pagination={false}
                hideFooter={true}
                disableSelectionOnClick
                density="compact"
                sx={{ 
                  border: 'none',
                  height: '100%'
                }}
              />
            </Box>
          )}
        </Box>
        
        {/* Context menu removed - direct chip clicking implemented */}
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
        <Button 
          variant="contained" 
          onClick={onReset}
          startIcon={<RefreshIcon />}
          sx={{ 
            textTransform: 'none', 
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 4,
            py: 1.2,
            fontSize: '1rem',
            bgcolor: 'wyspur.blue',
            '&:hover': {
              bgcolor: 'wyspur.purple', // Purple hover state as specified
            }
          }}
        >
          Start New Filter
        </Button>
        
        {/* Only show save button when we have results */}
        {filterResults && (
          <Button 
            variant="outlined" 
            onClick={handleSaveClick}
            startIcon={<BookmarkIcon />}
            sx={{ 
              textTransform: 'none', 
              borderRadius: '4px',
              fontWeight: 'medium',
              px: 4,
              py: 1.2,
              fontSize: '1rem',
              border: '1px solid #0095FF',
              color: '#0095FF',
              '&:hover': {
                borderColor: '#AB47BC',
                color: '#AB47BC'
              }
            }}
          >
            Save Filter
          </Button>
        )}
      </Box>
      
      {/* Save Filter Dialog */}
      <SaveFilterDialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)}
        filterId={filterId}
        filterConfig={standardizedFilterConfig}
        overrides={getCurrentOverrides()}
      />
    </Box>
  );
});

// Force redeploy: Added enhanced localStorage column instruction handling 
export default FilterResults;